import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { ThemeContext } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

import api from 'services/api';
import { show } from 'store/modules/dialog/actions';
import { updateFiltro } from 'store/modules/usuario/actions';
import { INITIAL_STATE } from 'store/modules/usuario/reducer';
import {
  DataFilter,
  Avaliacaoeng,
  Status,
  Tipo,
  Setor,
  StatusLocucao,
} from '../../utils/variables';
import {
  Chamada,
  ChamadaEmissora,
  Emissora,
  PatrocinioNormalized,
  Usuario,
} from 'pages/@types';
import AppError from 'utils/AppError';

import Card from 'components/Card';
import Button from 'components/Button';
import Widget from 'components/Widget';
import Flag from 'components/Flag';
import Tooltip from 'components/Tooltip';
import Pagination from 'components/Pagination';
import Radio from 'components/Radio';
import DropDownMenu from 'components/DropDownMenu';
import Aprovacao from './aprovacao';
import Checkbox from 'components/Checkbox';
import Input from 'components/Input';
import AsyncSelect from 'components/AsyncSelect';
import Avatar from 'components/Avatar';
import Loader from 'components/ContentLoader/ChamadaLoader';
import {
  FiBookOpen,
  FiAlertTriangle,
  FiCheckCircle,
  FiCoffee,
  FiGlobe,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCopy,
  FiChevronRight,
  FiUploadCloud,
  FiMic,
} from 'react-icons/fi';
import { FaScrewdriver } from 'react-icons/fa';

import { Table } from 'styles/global';
import {
  ContentEditor,
  Container,
  WidgetContent,
  FilterContent,
  FilterItem,
  Td,
  TdFlag,
  FormFilter,
  Th,
  Link,
  CenteredTableData,
} from './styles';
import { useModal } from 'hooks/modal';
import getValidationErrors from 'services/getValidationErros';
import Comunicador from './modalcomunicador';
import AsyncSelectCreatable from 'components/AsyncSelectCreatable';

interface EditorOption extends Usuario {
  label: string;
  value: number;
}

interface Filtro {
  status: string;
  editor: EditorOption;
  setor: string;
  tipo: string[];
  emissoras: string[];
  retranca: {
    value: string;
    label: string;
    __isNew__: boolean;
  };
}

interface EmissoraOption {
  id: number;
  label: string;
}
interface ChamadaOption extends Chamada {
  label: string;
  __isNew__: boolean;
}
interface Workflow {
  emprocesso: { count: number; total: number };
  aprovacao: { count: number; total: number };
  alteracao: { count: number; total: number };
  aprovado: { count: number; total: number };
  entregue: { count: number; total: number };
  finalizado: { count: number; total: number };
}
export default function Chamadas() {
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  const { profile, filtro } = useSelector((state: any) => state.usuario);
  /**
   * VARIAVEIS
   */
  const [workflow, setWorkflow] = useState<Workflow>({} as Workflow);
  const [chamadas, setChamadas] = useState<Chamada[]>([]);
  const [emissoras, setEmissoras] = useState<EmissoraOption[]>([]);
  const [selected, setSelected] = useState<Chamada>({} as Chamada);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingEmissoras, setLoadingEmissoras] = useState(false);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [oldFiltro, setOldFiltro] = useState<Filtro>({} as Filtro);
  const { showModal, hideModal } = useModal();

  // O null significa que ainda não foi ordenado. Mostrando a tela inicial original (sendo null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );

  const handleSort = () => {
    setSortDirection(prev => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
  };

  const chamadasOrdenadas = useMemo(() => {
    // Quando não houver ordenação, puxa a lista original
    if (sortDirection === null) return chamadas;

    return [...chamadas].sort((a, b) =>
      sortDirection === 'asc'
        ? new Date(a.datainicio).getTime() - new Date(b.datainicio).getTime()
        : new Date(b.datainicio).getTime() - new Date(a.datainicio).getTime(),
    );
  }, [chamadas, sortDirection]);

  const selectedId = useRef(0);
  /**
   * REF
   */
  const formFilterRef = useRef<FormHandles>(null);
  const formLinkRef = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */
  const handleSuccessMessage = useCallback(
    ({
      contentHTML,
      redirectTo,
    }: {
      contentHTML: string;
      redirectTo: string;
    }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const buscaChamadaRetranca = useCallback(async value => {
    if (value.length > 2) {
      setAsyncLoading(true);

      const response = await api.get('/chamadasts/retranca', {
        params: { retranca: value },
      });

      setAsyncLoading(false);
      return response.data.map((ch: Chamada) => ({
        value: ch.id,
        label: ch.descricao ? `${ch.retranca} (${ch.descricao})` : ch.retranca,
        retranca: ch.retranca,
        tipo: ch.tipo,
        setor: ch.setor,
        editor: ch.editor
          ? { value: ch.editor.id, label: ch.editor.nome }
          : null,
        status: ch.status,
        createdAt: ch.created_at
          ? format(parseISO(ch.created_at), 'dd/MM/yyyy H:m')
          : '',
        __isNew__: false,
      }));
    }
  }, []);

  const loadUsuarios = useCallback(async value => {
    // if (value.length > 3) {
    try {
      setAsyncLoading(true);

      const response = await api.get('/usuarios', {
        params: {
          nome: value,
          setor: 'promo,hub',
        },
      });
      setAsyncLoading(false);

      return response.data.docs.map((usu: Usuario) => ({
        value: usu.id,
        label: usu.nome,
        ...usu,
      }));
    } catch (err) {
      console.log(err);
      setAsyncLoading(false);
    }

    // }
  }, []);

  // const handleNovaChamada = useCallback(() => {
  //   navigate('/chamadas/novo');
  // }, []);

  // const handleEditCC = useCallback(ch => {
  //   navigate(`/closedcaptioneditor/${ch.midia.id}`);
  // }, []);

  // const handleEdit = useCallback(ch => {
  //   navigate(`/chamadas/${ch.id}`);
  // }, []);

  // const handleKanban = useCallback(ch => {
  //   navigate(`/chamadas/${ch.id}/kanban`);
  // }, []);

  const handleClick = useCallback(
    status => {
      setPage(1);
      // Se alterar o status, limpa o filtro de pendenteeng
      if (formFilterRef.current)
        formFilterRef.current.setFieldValue('pendenteeng', []);
      dispatch(
        updateFiltro({
          ...filtro,
          pendenteeng: false,
          status: filtro.status === status ? '' : status,
        }),
      );

      const path =
        filtro.status !== status && status === 'aprovacao,aprovado'
          ? '/chamadas/aprovacao'
          : '/chamadas';

      navigate(path);
    },
    [dispatch, filtro, navigate],
  );

  const handleDelete = useCallback(
    ch => {
      setSelected(ch);
      const optionBase = {
        type: 'question',
        contentHTML: `Remover <b>${ch.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/chamadas/${ch.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Chamada removida!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleChangeStatus = useCallback(
    async ch => {
      try {
        setLoading(true);
        const optionBase = {
          type: 'question',
          contentHTML: `Enviar <b>${ch.retranca}</b> para aprovação?`,
          confirmButtonText: 'CONFIRMAR',
          callback: {
            url: `/chamadas/${ch.id}`,
            method: api.put,
            data: {
              status: Status.APROVACAO,
            },
            action: 'aprovacao',
          },
          haveCallback: true,
          successMessage: `${ch.tipo} ${ch.retranca} enviada para aprovação!`,
          cancelButtonText: 'NÃO',
          showCancelButton: false,
          showConfirmButton: true,
          showCloseButton: true,
        };
        setSelected(ch);
        dispatch(show(optionBase));
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const handleCopy = useCallback(
    ch => {
      const optionBase = {
        type: 'question',
        contentHTML: `Duplicar <b>${ch.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        inputType: 'text',
        inputValue: ch.retranca,
        maxlength: '100',
        callback: {
          url: `/chamadas`,
          method: api.post,
          data: {
            ...ch,
            programa_id: ch.programa ? ch.programa.id : null,
            emissora: ch.chamadaemissora.map(
              (ce: ChamadaEmissora) => ce.emissora.id,
            ),
          },
          action: 'update',
        },
        haveCallback: true,
        successMessage: `${ch.tipo} ${ch.retranca} duplicada!`,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      setSelected(ch);
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleActionSelect = useCallback(
    (ch, action) => {
      switch (action.value) {
        case 1:
          handleCopy(ch);
          break;
        case 2:
          handleDelete(ch);
          break;
        case 3:
          handleChangeStatus(ch);
          break;
        default:
          break;
      }
    },
    [handleChangeStatus, handleCopy, handleDelete, navigate],
  );

  const handleSubmitLink = useCallback(
    async (id: number, linkhomerpc: string | null) => {
      await api
        .patch(`/chamadasts/${id}/linkhomerpc`, { linkhomerpc })
        .then(response => {
          if (
            response &&
            response.data &&
            response.data.message === 'success'
          ) {
            setChamadas(prevState =>
              prevState.map(c =>
                c.id === id ? { ...c, linkhomerpc: linkhomerpc } : c,
              ),
            );
            toast.success('Link atualizado com sucesso');
          } else {
            toast.error('Falha ao salvar o link');
          }
        })
        .catch(e => {
          if (e instanceof Error) toast.error(e.message);
          else if (e instanceof AppError) toast.error(e.message);
          else {
            const appError = e as AppError;
            if (appError.message) toast.error(appError.message);
            else toast.error('Erro inesperado');
          }
        });

      hideModal();
    },
    [hideModal],
  );

  const renderLinkHome = useCallback(
    ch => {
      const handleValidateSubmit = async (data: { linkhomerpc: string }) => {
        if (formLinkRef.current) {
          try {
            formLinkRef.current.setErrors({});

            const schema = Yup.object().shape({
              linkhomerpc: Yup.string()
                .url('Informe uma URL válida')
                .required('Informe a URL da matéria'),
            });

            await schema.validate({ ...data }, { abortEarly: false });
            handleSubmitLink(ch.id, data.linkhomerpc);
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              const errors = getValidationErrors(error);
              formLinkRef.current.setErrors(errors);
            } else if (error instanceof Error) {
              toast.error(error.message);
            }
          }
        }
      };
      return !ch.linkhomerpc ? (
        <Form ref={formLinkRef} onSubmit={handleValidateSubmit}>
          <Input name="linkhomerpc" placeholder="Link *" max={280} />

          <div className="actions">
            <Button color="default" onClick={() => hideModal()}>
              CANCELAR
            </Button>
            <Button type="submit" disabled={ch.linkhomerpc} color="primary">
              CONFIRMAR
            </Button>
          </div>
        </Form>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Link href={ch.linkhomerpc}>{ch.linkhomerpc}</Link>
          <Button
            fill
            color="font"
            round
            onClick={() => handleSubmitLink(ch.id, null)}
          >
            <FiTrash2 />
          </Button>
        </div>
      );
    },
    [handleSubmitLink, hideModal],
  );

  const handleLinkHomeRPC = useCallback(
    ch => {
      // e.preventDefault();
      showModal({
        title: `LINK - ${ch.retranca}`,
        component: renderLinkHome(ch),
      });
    },
    [renderLinkHome, showModal],
  );

  const renderUploadPlayer = useCallback(
    (id: number) => {
      const handleSubmit = async () => {
        try {
          const response = await api.patch(`/chamadasts/${id}/uploadplayer`);
          if (
            response &&
            response.data &&
            response.data.message === 'success'
          ) {
            if (filtro.status === Status.ENTREGUE)
              setChamadas(prevState => prevState.filter(p => p.id !== id));
            else
              setChamadas(prevState =>
                prevState.map(p =>
                  p.id === id ? { ...p, status: 'finalizado' } : p,
                ),
              );
            setWorkflow(prevState => ({
              ...prevState,
              entregue: {
                count: prevState.entregue.count - 1,
                total: prevState.entregue.total - 1,
              },
              finalizado: {
                count: prevState.finalizado.count + 1,
                total: prevState.finalizado.total + 1,
              },
            }));
            toast.success(`Upload confirmado`);
            hideModal();
          }
        } catch (e) {
          if (e instanceof Error) toast.error(e.message);
          else if (e instanceof AppError) toast.error(e.message);
          else {
            const appError = e as AppError;
            if (appError.message) toast.error(appError.message);
            else toast.error('Erro inesperado');
          }
        }
      };
      return (
        <div>
          <Button onClick={() => handleSubmit()} color="primary">
            CONFIRMAR
          </Button>
        </div>
      );
    },
    [filtro.status, hideModal],
  );

  const handleUploadPlayer = useCallback(
    ch => {
      try {
        if (ch.id)
          showModal({
            title: `Confirmar upload de ${ch.retranca} para a VATI?`,
            component: renderUploadPlayer(ch.id),
          });
      } catch (error) {
        console.log(error);
      }
    },
    [renderUploadPlayer, showModal],
  );

  const handleLocucao = useCallback(
    ch => {
      setSelected(ch);
      let optionBase = {
        type: 'question',
        contentHTML: `<b>${ch.retranca}</b><br/>${ch.locucao ||
          (ch.patrocinios && ch.patrocinios.length
            ? `${ch.locucaobase}<br/>${ch.patrocinios
                .map(
                  (p: PatrocinioNormalized, i: number) =>
                    `<b>Patrocínio ${i + 1}:</b>${p.locucao}`,
                )
                .join('<br/>')}`
            : 'Mídia sem locução')}`,
        confirmButtonText: 'OK',
        showConfirmButton: true,
        showCloseButton: true,
        haveCallback: false,
        cancelButtonText: '',
        showCancelButton: false,
        callback: {},
        successMessage: '',
      };
      if (ch.statuslocucao === StatusLocucao.NA) {
        optionBase = {
          ...optionBase,
          contentHTML: `Solicita locução de:<br/>${optionBase.contentHTML}`,
          haveCallback: true,
          cancelButtonText: 'NÃO',
          showCancelButton: false,
          callback: {
            url: `/chamadasts/${ch.id}/statuslocucao`,
            method: api.patch,
            data: { statuslocucao: StatusLocucao.SOLICITADO },
            action: 'locucao',
          },
          successMessage: 'Locução solicitada!',
        };
      }
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  /**
   * USE EFFECT
   */

  useEffect(() => {
    // Se entrar em chamadas/aprovacao, atualiza o filtro de status para "aprovacao,aprovado"
    if (location.pathname.includes('/aprovacao'))
      dispatch(
        updateFiltro({
          ...filtro,
          status: 'aprovacao,aprovado',
        }),
      );
    // Array de dependências intencionalmente vazio.
    // Não desejo que rode sempre que o filtro mudar,
    // apenas quando a página carrega pela primeira vez
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function loadChamadas() {
      setLoading(true);
      try {
        const response = await api.get('/chamadasts', {
          signal: controller.signal,
          params: {
            status: filtro.status,
            ...(filtro.tipo &&
              filtro.tipo.length && { tipo: filtro.tipo.join() }),
            ...(filtro.emissoras &&
              filtro.emissoras.length && {
                emissoras: filtro.emissoras.join(),
              }),
            ...(filtro.editor && { editor_id: filtro.editor.value }),
            ...(filtro.retranca && { retranca: filtro.retranca.value }),
            ...(filtro.setor && { setor: filtro.setor }),
            ...(filtro.setorsolicitante && {
              setorsolicitante: filtro.setorsolicitante,
            }),
            ...(filtro.pendenteeng && { avaliacaoeng: Avaliacaoeng.PENDENTE }),
            page,
          },
        });

        if (response && response.data) {
          setChamadas(response.data.chamadas);
          setPages(response.data.pages);
          setWorkflow(response.data.workflow);
        }
        // else toast.error('Falha de comunicação com o sistema');
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
        setLoading(false);
      }
    }
    loadChamadas();
    return () => {
      controller.abort();
    };
  }, [filtro, page]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadEmissoras() {
      setLoadingEmissoras(true);
      try {
        if (emissoras.length === 0) {
          const response = await api.get('/emissoras');
          if (response && response.data) {
            setEmissoras(
              response.data.map((e: Emissora) => ({
                id: e.siglapromo,
                label: e.siglapromo,
              })),
            );
          } else toast.error('Falha de comunicação com o sistema');
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingEmissoras(false);
      }
    }
    loadEmissoras();
    return () => {
      controller.abort();
    };
  }, [emissoras.length]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'locucao') {
        setChamadas(prevState =>
          prevState.map(p =>
            p.id === selected.id ? { ...p, statuslocucao: 'solicitado' } : p,
          ),
        );
      }
      if (
        (callbackDialog.success.action === 'success' ||
          callbackDialog.success.action === 'aprovacao') &&
        selected
      ) {
        setChamadas(prevState => prevState.filter(c => c.id !== selected.id));
      }

      if (
        callbackDialog.success.action === 'update' &&
        callbackDialog.success.data &&
        selected
      ) {
        handleSuccessMessage({
          contentHTML: `<h3>Material duplicado!</h3> <p>Você será redirecionado para edição em 3 segundos</p>`,
          redirectTo: `/chamadas/${callbackDialog.success.data.id}`,
        });

        setChamadas(prevState => [
          ...prevState,
          {
            ...callbackDialog.success.data,
            chamadaemissora: callbackDialog.success.data.emissora.map(
              (e: Emissora) => ({
                emissora: e,
              }),
            ),
          },
        ]);
      }
      if (
        callbackDialog.success.action === 'avaliar' &&
        callbackDialog.success.data &&
        selected
      ) {
        if (
          !!callbackDialog.success.data.finalizarAvaliacao &&
          callbackDialog.success.data.finalizarAvaliacao.status === 'aprovado'
        ) {
          const options = {
            type: 'info',
            contentHTML: `Retranca ${selected.retranca} aprovada por todos avaliadores, você será redirecionado para finalizar as informações do material no kanban`,
            redirectTo: `/chamadas/${selected.id}/kanban`,
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
          };
          setTimeout(() => dispatch(show(options)), 2000);
        } else {
          setChamadas(prevState =>
            prevState.map(c =>
              c.id === selected.id
                ? {
                    ...c,
                    chamadaaprovador: c.chamadaaprovador.map(a =>
                      a.usuario.id === callbackDialog.success.data.usuario_id
                        ? {
                            ...a,
                            status: callbackDialog.success.data.status,
                            observacao: callbackDialog.success.data.observacao,
                          }
                        : a,
                    ),
                  }
                : c,
            ),
          );
        }
      }
    }
  }, [callbackDialog, dispatch, handleSuccessMessage, selected]);

  const disableEdit = useCallback(
    (ch: Chamada) => {
      if (loading || asyncLoading) return true;
      if (
        [Status.ENTREGUE, Status.FINALIZADO].includes(ch.status) ||
        (profile.setor === Setor.OPEC && ch.tipo === Tipo.VINHETA && ch.midia)
      )
        return true;
      // if (
      //   ch.editor &&
      //   ch.editor.id !== profile.id &&
      //   !ch.aprovadores.some(
      //     (a: ChamadaAprovador) => a.usuario.id === profile.id,
      //   )
      // )
      //   return true;
      // if (
      //   profile.role !== Role.ADMIN &&
      //   profile.role !== Role.PROMO &&
      //   (profile.role !== Role.OPEC ||
      //     (profile.role === Role.OPEC && ch.tipo !== Tipo.VINHETA))
      // )
      //   return true;
      return false;
    },
    [asyncLoading, loading, profile.setor],
  );

  const handleComunicadores = useCallback(
    ch => {
      selectedId.current = ch.id;
      showModal({
        title: `${ch.retranca}`,
        component: (
          <Comunicador setChamadas={setChamadas} selectedId={selectedId} />
        ),
        width: '30vw',
      });
    },
    [showModal],
  );
  function Lista() {
    return (
      <Card>
        <header>
          <h4>LISTA DE CHAMADAS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            href={'/chamadas/novo'}
            tooltip="Criar nova chamada"
          >
            NOVA CHAMADA <FiPlus />
          </Button>
        </header>
        {loading || loadingEmissoras ? (
          <Loader />
        ) : (
          // <Loader />
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>RETRANCA</th>
                  <th style={{ width: 170, textAlign: 'center' }}>TIPO</th>
                  <th style={{ width: 80, textAlign: 'center' }}>CM</th>
                  <th style={{ width: 80 }}>DUR.</th>
                  <Th style={{ width: 200 }} $hide="desktop">
                    PROGRAMA
                  </Th>
                  <Th
                    style={{ width: 160, cursor: 'pointer' }}
                    onClick={handleSort}
                  >
                    VEICULAÇÃO{' '}
                    {sortDirection === null
                      ? '⇅'
                      : sortDirection === 'asc'
                      ? '▲'
                      : '▼'}
                  </Th>
                  <th style={{ width: 70 }}>EDITOR</th>
                  <th style={{ textAlign: 'center', width: 70 }}>LINK</th>
                  <th style={{ width: 70 }}>LOC.</th>
                  <th style={{ textAlign: 'right', width: 120 }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {!!chamadasOrdenadas.length &&
                  chamadasOrdenadas.map((ch, index) => (
                    <tr key={ch.id}>
                      <TdFlag
                        size="2"
                        // Caso o status seja APROVADO, envia aprovação para a flag ficar laranja ao invés de verde
                        type={
                          ch.status === Status.APROVADO
                            ? 'aprovacao'
                            : ch.status === Status.ENTREGUE
                            ? 'entreguech'
                            : ch.status
                        }
                        $hasMidia={!!ch.midia}
                        $avaliacaoEngPendente={
                          ch.status === Status.ENTREGUE &&
                          ch.midia &&
                          ch.midia.avaliacaoeng === Avaliacaoeng.PENDENTE
                        }
                      >
                        <div id={`tdFlag_id${index}`} />
                        <Tooltip target={`tdFlag_id${index}`} position="right">
                          <p>
                            <strong>STATUS:</strong> {ch.status}
                            {!ch.midia && ' (Sem mídia)'}
                          </p>
                        </Tooltip>
                      </TdFlag>
                      <Td>
                        {ch.retranca}
                        {ch.descricao && <strong>({ch.descricao})</strong>}
                      </Td>
                      <td>
                        <Flag type={ch.tipo} name={ch.tipo} />
                      </td>
                      <td style={{ textAlign: 'center', paddingRight: 5 }}>
                        {[Tipo.CHAMADA, Tipo.AFINACAO].includes(ch.tipo)
                          ? '---'
                          : ch.codmaterial}
                      </td>
                      <td>{ch.duracao} s</td>
                      <Td $hide="desktop">
                        {ch.programa ? ch.programa.mnemonico : '---'}
                      </Td>
                      <Td $hide="laptopM" id={`id_datas_${ch.id}`}>
                        {ch.datainicio && ch.datafim
                          ? `de ${format(
                              parseISO(ch.datainicio),
                              'dd/MM',
                            )} a ${format(parseISO(ch.datafim), 'dd/MM')}`
                          : '---'}
                      </Td>
                      {ch.chamadaemissora && ch.chamadaemissora.length > 0 && (
                        <Tooltip target={`id_datas_${ch.id}`}>
                          {ch.chamadaemissora.length === 8
                            ? 'REDE'
                            : ch.chamadaemissora
                                .map(emi => emi.emissora.siglapromo)
                                .join(', ')}
                        </Tooltip>
                      )}
                      <CenteredTableData>
                        <Avatar
                          size="40"
                          name={ch.editor?.nome}
                          src={ch.editor?.avatar_url}
                          round
                        />
                      </CenteredTableData>
                      <td>
                        <Button
                          style={{ textAlign: 'center', margin: ' 0 auto' }}
                          id={`id_link_${ch.id}`}
                          fill
                          color={ch.linkhomerpc ? 'success' : 'font'}
                          round
                          disabled={[
                            Tipo.CHAMADA,
                            Tipo.VINHETA,
                            Tipo.AFINACAO,
                            Tipo.CHPATROCINADA,
                          ].includes(ch.tipo)}
                          onClick={() => {
                            handleLinkHomeRPC(ch);
                          }}
                        >
                          <FiGlobe />
                        </Button>
                      </td>
                      <td>
                        <Button
                          id={`id_locucao_${ch.id}`}
                          fill
                          color={
                            ch.statuslocucao === StatusLocucao.GRAVADO
                              ? 'success'
                              : ch.statuslocucao === StatusLocucao.SOLICITADO
                              ? 'warn'
                              : 'font'
                          }
                          round
                          disabled={
                            [Status.FINALIZADO, Status.ENTREGUE].includes(
                              ch.status,
                            ) ||
                            (ch.comunicador &&
                              !ch.locucao &&
                              (!ch.patrocinios.length ||
                                !ch.patrocinios?.every(p => p.locucao)))
                          }
                          onClick={() => {
                            if (!ch.comunicador) {
                              handleComunicadores(ch);
                            } else {
                              handleLocucao(ch);
                            }
                          }}
                        >
                          <FiMic />
                          <Tooltip target={`id_locucao_${ch.id}`}>
                            {ch.statuslocucao === StatusLocucao.GRAVADO
                              ? 'GRAVAÇÃO FINALIZADA'
                              : ch.statuslocucao === StatusLocucao.SOLICITADO
                              ? 'AGUARDANDO GRAVAÇÃO'
                              : !ch.comunicador
                              ? 'SEM COMUNICADOR'
                              : ch.locucao ||
                                (ch.patrocinios.length &&
                                  ch.patrocinios?.every(p => p.locucao))
                              ? 'SOLICITAR GRAVAÇÃO'
                              : 'SEM LOCUÇÃO'}
                          </Tooltip>
                        </Button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', width: 120 }}>
                          <Button
                            id={`id_edit_${ch.id}`}
                            fill
                            color="font"
                            round
                            href={`/chamadas/${ch.id}/kanban`}
                          >
                            <FiChevronRight />
                          </Button>
                          <Tooltip target={`id_edit_${ch.id}`}>
                            Ir para Kanban
                          </Tooltip>
                          {ch.status === Status.ENTREGUE ? (
                            <>
                              <Button
                                id={`id_${ch.id}`}
                                fill
                                color="font"
                                round
                                onClick={() => handleUploadPlayer(ch)}
                                disabled={
                                  ch.midia.avaliacaoeng !==
                                    Avaliacaoeng.APROVADO &&
                                  ch.midia.avaliacaoeng !==
                                    Avaliacaoeng.RESSALVA
                                }
                              >
                                <FiUploadCloud />
                              </Button>
                              <Tooltip target={`id_${ch.id}`}>
                                {ch.midia.avaliacaoeng !==
                                  Avaliacaoeng.APROVADO &&
                                ch.midia.avaliacaoeng !== Avaliacaoeng.RESSALVA
                                  ? `Avaliação Eng pendente`
                                  : `Confirmar Upload (Vati)`}
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Button
                                id={`id_${ch.id}`}
                                fill
                                color="font"
                                round
                                onClick={() => handleCopy(ch)}
                              >
                                <FiCopy />
                              </Button>
                              <Tooltip target={`id_${ch.id}`}>DUPLICAR</Tooltip>
                            </>
                          )}
                          <DropDownMenu
                            id={`acoes${index}`}
                            disabled={false}
                            actions={[
                              {
                                value: 0,
                                label: 'EDITAR',
                                disabled: disableEdit(ch),
                                icon: <FiEdit />,
                                href: `/chamadas/${ch.id}`,
                              },
                              {
                                value: 1,
                                label: 'DUPLICAR',
                                icon: <FiCopy />,
                              },
                              {
                                value: 2,
                                label: 'APAGAR',
                                disabled: ch.status === Status.FINALIZADO,
                                icon: <FiTrash2 />,
                              },
                              {
                                value: 3,
                                label: 'ENV APROV.',
                                disabled:
                                  ch.status !== Status.EMPROCESSO &&
                                  ch.status !== Status.ALTERACAO,
                                icon: <FiCheckCircle />,
                              },
                              {
                                value: 0,
                                label: 'EDITAR CC',
                                disabled:
                                  !ch.midia ||
                                  ![
                                    Status.EMPROCESSO,
                                    Status.APROVACAO,
                                  ].includes(ch.status) ||
                                  ch.midia.statuscaption !== 'edicao',
                                icon: <FaScrewdriver />,
                                href: ch.midia
                                  ? `/closedcaptioneditor/${ch.midia.id}`
                                  : '',
                              },
                            ]}
                            handleClick={action =>
                              !action.disabled && handleActionSelect(ch, action)
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Container>
      <WidgetContent>
        <Widget
          title="Em processo"
          type="condensed"
          icon={<FiBookOpen />}
          active={filtro.status === 'emprocesso'}
          color="info"
          loading={loading}
          handleClick={() => {
            if (!filtro.retranca) handleClick('emprocesso');
          }}
          data={[
            workflow.emprocesso ? workflow.emprocesso.count : 0,
            workflow.emprocesso ? workflow.emprocesso.total : 0,
          ]}
        />
        <Widget
          title="Aprovação"
          type="condensed"
          to="/chamadas/aprovacao"
          icon={<FiCoffee />}
          active={filtro.status === 'aprovacao,aprovado'}
          color="warn"
          loading={loading}
          handleClick={() => {
            if (!filtro.retranca) handleClick('aprovacao,aprovado');
          }}
          data={[
            workflow.aprovacao ? workflow.aprovacao.count : 0,
            workflow.aprovacao ? workflow.aprovacao.total : 0,
          ]}
        />
        <Widget
          title="Alteração"
          type="condensed"
          icon={<FiAlertTriangle />}
          active={filtro.status === 'alteracao'}
          color="danger"
          loading={loading}
          handleClick={() => {
            if (!filtro.retranca) handleClick('alteracao');
          }}
          data={[
            workflow.alteracao ? workflow.alteracao.count : 0,
            workflow.alteracao ? workflow.alteracao.total : 0,
          ]}
        />

        <Widget
          title="Entregue"
          type="condensed"
          icon={<FiCheckCircle />}
          active={filtro.status === 'entregue'}
          color="done"
          loading={loading}
          handleClick={() => {
            if (!filtro.retranca) handleClick('entregue');
          }}
          data={[
            workflow.entregue ? workflow.entregue.count : 0,
            workflow.entregue ? workflow.entregue.total : 0,
          ]}
        />
        <Widget
          title="Finalizado"
          type="condensed"
          icon={<FiCheckCircle />}
          active={filtro.status === 'finalizado'}
          color="success"
          loading={loading}
          handleClick={() => {
            if (!filtro.retranca) handleClick('finalizado');
          }}
          data={[
            workflow.finalizado ? workflow.finalizado.count : 0,
            workflow.finalizado ? workflow.finalizado.total : 0,
          ]}
        />
      </WidgetContent>

      <FilterContent>
        <span> FILTROS </span>
        <FormFilter
          onSubmit={() => {}}
          ref={formFilterRef}
          initialData={filtro}
        >
          <FilterItem>
            <legend>Setor</legend>
            <Radio
              // id="filtro_setorsolicitante_id"
              name="setor"
              columns="1fr"
              direction="column"
              options={DataFilter.setores}
              handleSelected={async value => {
                setPage(1);
                dispatch(updateFiltro({ ...filtro, setor: value.id }));

                // const editor = await loadUsuarios()

                // const editorField = await formFilterRef.current.getFieldRef(
                //   'editor'
                // )
                // editorField.setState({ defaultOptions: editor })
              }}
              disabled={loading || !!filtro.retranca}
            />
          </FilterItem>
          <FilterItem $hide="laptop">
            <legend>Tipos</legend>
            <Checkbox
              // id="filtro_tipo_id"
              name="tipo"
              columns="1fr 1fr 1fr 1fr"
              options={DataFilter.tipos}
              handleSelected={value => {
                setPage(1);
                if (value.checked) {
                  dispatch(
                    updateFiltro({
                      ...filtro,
                      tipo: filtro.tipo
                        ? [...filtro.tipo, value.id]
                        : [value.id],
                    }),
                  );
                } else {
                  dispatch(
                    updateFiltro({
                      ...filtro,
                      tipo: filtro.tipo
                        ? filtro.tipo.filter(
                            (item: string) => item !== value.id,
                          )
                        : [],
                    }),
                  );
                }
              }}
              disabled={loading || !!filtro.retranca}
            />
          </FilterItem>
          <FilterItem $hide="desktop">
            <legend>Praças</legend>
            <Checkbox
              // id="filtro_unidade_id"
              name="emissoras"
              columns="1fr 1fr 1fr"
              options={emissoras}
              disabled={loading || !!filtro.retranca}
              handleSelected={value => {
                setPage(1);
                if (value.checked) {
                  dispatch(
                    updateFiltro({
                      ...filtro,
                      emissoras: filtro.emissoras
                        ? [...filtro.emissoras, value.id]
                        : [value.id],
                    }),
                  );
                } else {
                  dispatch(
                    updateFiltro({
                      ...filtro,
                      emissoras: filtro.emissoras
                        ? filtro.emissoras.filter(
                            (item: Emissora) => item !== value.id,
                          )
                        : [],
                    }),
                  );
                }
              }}
            />
          </FilterItem>
          <section>
            <p>Retranca</p>
            <ContentEditor>
              <AsyncSelectCreatable
                fill
                searchable
                menuPlacement="bottom"
                name="retranca"
                loadOptions={buscaChamadaRetranca}
                placeholder="Digite o titulo da retranca"
                isValidNewOption={(inputValue: string) => inputValue.length > 1}
                errorField=""
                onChange={(value: ChamadaOption) => {
                  setPage(1);
                  if (value) {
                    let tempFilter;
                    if (value.__isNew__) {
                      tempFilter = {
                        status: null,
                        setor: '',
                        editor: null,
                        tipo: DataFilter.tipos.map(t => t.id),
                        emissoras: emissoras.map(e => e.label),
                        retranca: {
                          value: value.label,
                          label: value.label,
                          __isNew__: value.__isNew__,
                        },
                      };
                    } else {
                      tempFilter = {
                        status:
                          value.status === 'aprovacao' ||
                          value.status === 'aprovado'
                            ? 'aprovacao,aprovado'
                            : value.status,
                        editor: value.editor,
                        setor: value.setor,
                        tipo: [value.tipo],
                        emissoras: emissoras.map(e => e.label),
                        retranca: {
                          value: value.retranca,
                          label: value.retranca,
                          __isNew__: value.__isNew__,
                        },
                      };
                    }

                    if (formFilterRef.current) {
                      formFilterRef.current.setFieldValue(
                        'editor',
                        tempFilter.editor,
                      );
                      formFilterRef.current.setFieldValue(
                        'tipo',
                        tempFilter.tipo,
                      );
                      formFilterRef.current.setFieldValue(
                        'setor',
                        tempFilter.setor,
                      );
                      formFilterRef.current.setFieldValue(
                        'emissoras',
                        tempFilter.emissoras,
                      );
                    }

                    setOldFiltro(filtro);
                    dispatch(updateFiltro({ ...tempFilter }));
                  } else {
                    if (oldFiltro) {
                      if (formFilterRef.current) {
                        formFilterRef.current.setFieldValue(
                          'emissoras',
                          oldFiltro.emissoras,
                        );
                        formFilterRef.current.setFieldValue(
                          'tipo',
                          oldFiltro.tipo,
                        );
                        formFilterRef.current.setFieldValue(
                          'editor',
                          oldFiltro.editor,
                        );
                        formFilterRef.current.setFieldValue(
                          'setor',
                          oldFiltro.setor,
                        );
                      }
                      dispatch(updateFiltro({ ...oldFiltro }));
                      setOldFiltro({} as Filtro);
                    } else {
                      // caso não tenha old filtro e ele tenta remover uma retranca,
                      // volta o filtro pro initial state
                      if (formFilterRef.current) {
                        formFilterRef.current.setFieldValue(
                          'setor',
                          INITIAL_STATE.filtro.setor,
                        );
                        formFilterRef.current.setFieldValue(
                          'tipo',
                          INITIAL_STATE.filtro.tipo,
                        );
                        formFilterRef.current.setFieldValue(
                          'editor',
                          INITIAL_STATE.filtro.editor,
                        );
                      }
                      dispatch(updateFiltro(INITIAL_STATE.filtro));
                    }
                  }
                }}
                createOptionPosition="first"
                type="filtroChamada"
                isClearable
                isMulti={false}
                formatCreateLabel={(inputValue: string) =>
                  `Buscar por " ${inputValue} "`
                }
                isDisabled={loading}
                isLoading={asyncLoading}
              />
              <Checkbox
                // id="filtro_opec_id"
                name="pendenteeng"
                options={DataFilter.pendenteeng}
                columns="auto"
                handleSelected={value => {
                  setPage(1);
                  if (value.checked) {
                    dispatch(
                      updateFiltro({
                        ...filtro,
                        pendenteeng: true,
                      }),
                    );
                  } else {
                    dispatch(
                      updateFiltro({
                        ...filtro,
                        pendenteeng: false,
                      }),
                    );
                  }
                }}
                disabled={
                  loading ||
                  filtro.retranca ||
                  filtro.status !== Status.ENTREGUE
                }
              />
            </ContentEditor>
            <p>Editor</p>
            <ContentEditor>
              <AsyncSelect
                fill
                searchable
                name="editor"
                type="avatar"
                loadOptions={loadUsuarios}
                placeholder="Digite o nome do editor"
                isClearable
                onChange={(value: Usuario) => {
                  setPage(1);
                  if (value) {
                    dispatch(updateFiltro({ ...filtro, editor: value }));
                  } else {
                    dispatch(updateFiltro({ ...filtro, editor: null }));
                  }
                }}
                isMulti={false}
                isDisabled={loading || !!filtro.retranca}
                isLoading={asyncLoading}
              />

              <Checkbox
                // id="filtro_opec_id"
                name="opec"
                options={DataFilter.opec}
                columns="auto"
                handleSelected={value => {
                  setPage(1);
                  if (value.checked) {
                    dispatch(
                      updateFiltro({
                        ...filtro,
                        setorsolicitante: 'opec',
                      }),
                    );
                  } else {
                    dispatch(
                      updateFiltro({
                        ...filtro,
                        setorsolicitante: null,
                      }),
                    );
                  }
                }}
                disabled={loading || filtro.retranca || !filtro.editor}
              />
            </ContentEditor>
          </section>
        </FormFilter>
      </FilterContent>

      {filtro.status !== 'aprovacao,aprovado' ? (
        <Lista />
      ) : (
        <Aprovacao
          setPage={(page: number) => setPage(page)}
          pages={pages}
          page={page}
          loading={loading}
          chamadas={chamadas}
          usuarioLogado={profile}
          handleActionSelect={handleActionSelect}
          handleCopy={handleCopy}
          setChamadas={setChamadas}
          handleLinkHomeRPC={handleLinkHomeRPC}
          disableEdit={disableEdit}
        />
      )}
    </Container>
  );
}
